import { useEffect, useMemo, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { apiBaseUrl } from '../../api/client'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { apiErrorCode, apiErrorDetail } from '../../lib/apiError'
import { useFormatters } from '../../lib/format'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Alert, AlertDescription } from '../ui/alert'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Skeleton } from '../ui/skeleton'
import { Textarea } from '../ui/textarea'
import { AttachDocumentControl } from '../documents/AttachDocumentControl'
import { OffersCard, ViewingsCard } from './LettingCards'
import { OutcomeBadge, ResultBadge, useLabel, useRefreshApplications, type Question, type RentalApplication } from './shared'

type Overrides = Record<string, { result: string; reason: string }>
const UNSET = '__unset__'

/** The application screen: members, answers with their verdict rows, documents, review, journal (spec 2026-10-25-11). */
export function RentalApplicationPage() {
    const { org, applicationUid } = useParams({ from: '/$org/rental-applications/$applicationUid' })
    const { t } = useTranslation('rentalApplications')
    const canWrite = useCan('rental_applications.write')
    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}', {
        params: { path: { orgCode: org, applicationUid } },
    })
    const app = data?.application
    const lead = app?.members.find((m) => m.role === 'lead_applicant')
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('title'), path: `/${org}/rental-applications` },
        { title: lead?.name ?? t('detail.title') },
    ])

    if (isLoading || !app) return <Skeleton className="h-64 w-full" />
    const closed = !!app.outcome || !!app.purged_at
    const editable = canWrite && !closed

    return (
        <div className="space-y-6" data-testid="rental-application-page" data-stage={app.stage} data-outcome={app.outcome ?? ''}>
            <PageHeader
                icon={SECTION_ICONS['rental-applications']}
                title={app.purged_at ? t('purged.name') : lead?.name ?? t('detail.title')}
                description={app.listing?.asset_name ?? ''}
                titleAside={
                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" data-testid="rental-application-stage">{t(`stage.${app.stage}`)}</Badge>
                        <OutcomeBadge outcome={app.outcome} testId="rental-application-outcome" />
                    </div>
                }
            />
            {app.purged_at ? (
                <Alert data-testid="rental-application-purged"><AlertDescription>{t('purged.description')}</AlertDescription></Alert>
            ) : (
                <>
                    {app.neutralised_count > 0 && (
                        <Alert variant="warning" data-testid="rental-application-neutralised-banner">
                            <AlertDescription>{t('neutralised.title', { count: app.neutralised_count })}</AlertDescription>
                        </Alert>
                    )}
                    <OutcomeCard app={app} />
                    <div className="grid gap-6 lg:grid-cols-2">
                        <MembersCard org={org} app={app} editable={editable} />
                        <ReviewPanel org={org} app={app} canWrite={editable} />
                    </div>
                    <AnswersCard org={org} app={app} editable={editable} />
                    <div className="grid gap-6 lg:grid-cols-2">
                        <ViewingsCard org={org} app={app} editable={editable} />
                        <OffersCard org={org} app={app} editable={editable} />
                    </div>
                    <DocumentsCard org={org} app={app} editable={editable} />
                    <div className="grid gap-6 lg:grid-cols-2">
                        <QuestionnaireLinkCard org={org} app={app} editable={editable} />
                        <PurgeCard org={org} app={app} canWrite={canWrite} />
                    </div>
                </>
            )}
            <EventsCard org={org} applicationUid={applicationUid} />
        </div>
    )
}

function OutcomeCard({ app }: { app: RentalApplication }) {
    const { t } = useTranslation('rentalApplications')
    const label = useLabel()
    if (app.outcome !== 'refused') return null
    const reason = app.screening.reasons.find((r) => r.code === app.outcome_reason_code)
    return (
        <Card data-testid="rental-application-contest">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('contest.title')}</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
                <p data-testid="rental-application-contest-reason">{t('contest.reason', { reason: label(reason?.label, app.outcome_reason_code ?? '') })}</p>
                {app.screening.release?.contest_route && (
                    <p className="text-muted-foreground" data-testid="rental-application-contest-route">{label(app.screening.release.contest_route)}</p>
                )}
                <p className="text-muted-foreground">{t('contest.legal_hold_hint')}</p>
            </CardContent>
        </Card>
    )
}

function MembersCard({ org, app, editable }: { org: string; app: RentalApplication; editable: boolean }) {
    const { t } = useTranslation('rentalApplications')
    const refresh = useRefreshApplications()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [role, setRole] = useState('co_applicant')
    const [error, setError] = useState<string | null>(null)
    const patch = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}', {
        onSuccess: () => {
            refresh()
            setName('')
            setEmail('')
            setError(null)
        },
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    const path = { orgCode: org, applicationUid: app.uid }
    return (
        <Card data-testid="rental-application-members">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('members.title')}</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
                {app.members.map((m) => (
                    <div key={m.uid} className="flex items-center justify-between gap-2" data-testid="rental-application-member-row" data-role={m.role}>
                        <div>
                            <span className="font-medium">{m.name}</span> <span className="text-muted-foreground">{m.email}</span>
                            <Badge variant="outline" className="ml-2">{t(`role.${m.role}`)}</Badge>
                        </div>
                        {editable && m.role !== 'lead_applicant' && (
                            <Button size="sm" variant="ghost" onClick={() => patch.mutate({ params: { path }, body: { remove_member_uids: [m.uid] } })} data-testid="rental-application-member-remove">
                                {t('members.remove')}
                            </Button>
                        )}
                    </div>
                ))}
                {editable && (
                    <div className="grid gap-2 border-t pt-2 md:grid-cols-4">
                        <Input placeholder={t('members.name')} value={name} onChange={(e) => setName(e.target.value)} data-testid="rental-application-member-name" />
                        <Input placeholder={t('members.email')} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="rental-application-member-email" />
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger data-testid="rental-application-member-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="co_applicant">{t('role.co_applicant')}</SelectItem>
                                <SelectItem value="guarantor">{t('role.guarantor')}</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button
                            size="sm"
                            disabled={!name.trim() || patch.isPending}
                            onClick={() => patch.mutate({ params: { path }, body: { add_members: [{ name, email: email || undefined, role: role as 'co_applicant' }] } })}
                            data-testid="rental-application-member-add"
                        >
                            {t('members.add')}
                        </Button>
                    </div>
                )}
                {error && <p className="text-destructive" data-testid="rental-application-members-error">{error}</p>}
            </CardContent>
        </Card>
    )
}

function AnswerInput({ q, value, onChange, disabled }: { q: Question; value: unknown; onChange: (v: unknown) => void; disabled: boolean }) {
    const { t } = useTranslation('rentalApplications')
    if (q.value_type === 'boolean') {
        return (
            <Select value={value === true ? 'true' : value === false ? 'false' : UNSET} onValueChange={(v) => onChange(v === UNSET ? null : v === 'true')} disabled={disabled}>
                <SelectTrigger data-testid="rental-application-answer-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={UNSET}>{t('answers.unanswered')}</SelectItem>
                    <SelectItem value="true">{t('answers.yes')}</SelectItem>
                    <SelectItem value="false">{t('answers.no')}</SelectItem>
                </SelectContent>
            </Select>
        )
    }
    if (q.value_type === 'enum') {
        return (
            <Select value={typeof value === 'string' ? value : UNSET} onValueChange={(v) => onChange(v === UNSET ? null : v)} disabled={disabled}>
                <SelectTrigger data-testid="rental-application-answer-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={UNSET}>{t('answers.unanswered')}</SelectItem>
                    {(q.enum_values ?? []).map((v) => <SelectItem key={v} value={v}>{t(`enum.${v}`, { defaultValue: v })}</SelectItem>)}
                </SelectContent>
            </Select>
        )
    }
    if (q.value_type === 'date_range') {
        const range = (value ?? {}) as { from?: string; to?: string }
        return (
            <div className="flex gap-2" data-testid="rental-application-answer-input">
                <Input type="date" value={range.from ?? ''} disabled={disabled} onChange={(e) => onChange({ ...range, from: e.target.value })} />
                <Input type="date" value={range.to ?? ''} disabled={disabled} onChange={(e) => onChange({ ...range, to: e.target.value })} />
            </div>
        )
    }
    return (
        <Input
            type="number"
            min={0}
            value={typeof value === 'number' ? String(value) : ''}
            disabled={disabled}
            placeholder={q.value_type === 'ratio' ? t('answers.monthly_income') : ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            data-testid="rental-application-answer-input"
        />
    )
}

function AnswersCard({ org, app, editable }: { org: string; app: RentalApplication; editable: boolean }) {
    const { t } = useTranslation('rentalApplications')
    const label = useLabel()
    const refresh = useRefreshApplications()
    const [member, setMember] = useState(app.members.find((m) => m.role === 'lead_applicant')?.uid ?? '')
    const [values, setValues] = useState<Record<string, unknown>>({})
    const [error, setError] = useState<string | null>(null)
    useEffect(() => setValues({ ...((app.declared_facts?.[member] ?? {}) as Record<string, unknown>) }), [app.declared_facts, member])
    const rows = useMemo(() => Object.fromEntries(app.screening.rows.map((r) => [r.criterion_code, r])), [app.screening.rows])
    const patch = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}', {
        onSuccess: () => {
            refresh()
            setError(null)
        },
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    const save = () => {
        const answers: Record<string, unknown> = {}
        for (const q of app.questionnaire) answers[q.criterion_code] = values[q.criterion_code] ?? null
        patch.mutate({ params: { path: { orgCode: org, applicationUid: app.uid } }, body: { answers: { [member]: answers } } })
    }
    return (
        <Card data-testid="rental-application-answers">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('answers.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                {app.screening.status === 'dpia_missing' && (
                    <Alert variant="warning" data-testid="rental-application-dpia-missing"><AlertDescription>{t('screening.dpia_missing')}</AlertDescription></Alert>
                )}
                {app.screening.status === 'no_release' && (
                    <Alert data-testid="rental-application-no-release"><AlertDescription>{t('screening.no_release')}</AlertDescription></Alert>
                )}
                {app.screening.release?.indicative && <p className="text-xs text-muted-foreground" data-testid="rental-application-indicative">{t('screening.indicative')}</p>}
                {app.screening.global && (
                    <div className="flex items-center gap-2" data-testid="rental-application-verdict">
                        <span>{t('screening.global')}</span>
                        <ResultBadge result={app.screening.global} testId="rental-application-verdict-badge" />
                        <span className="text-xs text-muted-foreground">{t('screening.orders_only')}</span>
                    </div>
                )}
                {app.questionnaire.length === 0 ? (
                    <p className="text-muted-foreground" data-testid="rental-application-no-questions">{t('answers.none')}</p>
                ) : (
                    <>
                        {app.members.length > 1 && (
                            <div className="max-w-xs">
                                <Label className="mb-1 block">{t('answers.member')}</Label>
                                <Select value={member} onValueChange={setMember}>
                                    <SelectTrigger data-testid="rental-application-answer-member"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {app.members.map((m) => <SelectItem key={m.uid} value={m.uid}>{m.name} · {t(`role.${m.role}`)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {app.questionnaire.map((q) => {
                            const row = rows[q.criterion_code]
                            return (
                                <div key={q.criterion_code} className="grid items-center gap-2 border-b pb-2 md:grid-cols-[1fr_14rem_1fr]" data-testid="rental-application-criterion-row" data-criterion={q.criterion_code} data-result={row?.result ?? ''}>
                                    <div>
                                        <div className="font-medium">{label(q.label, q.criterion_code)}</div>
                                        <div className="text-xs text-muted-foreground">{t(`policy.${q.policy}`, { defaultValue: q.policy })}</div>
                                    </div>
                                    <AnswerInput q={q} value={values[q.criterion_code]} disabled={!editable} onChange={(v) => setValues({ ...values, [q.criterion_code]: v })} />
                                    <div className="flex flex-wrap items-center gap-2">
                                        {row && <ResultBadge result={row.result} testId="rental-application-criterion-result" />}
                                        {row && (
                                            <span className="text-xs text-muted-foreground" data-testid="rental-application-criterion-explanation">
                                                {t(`explanation.${row.explanation}`, { defaultValue: row.explanation })}
                                                {row.computed != null ? ` (${t('answers.ratio', { ratio: row.computed })})` : ''}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                        {editable && (
                            <Button size="sm" onClick={save} disabled={patch.isPending} data-testid="rental-application-answers-save">{t('answers.save')}</Button>
                        )}
                    </>
                )}
                {error && <p className="text-destructive" data-testid="rental-application-answers-error">{error}</p>}
            </CardContent>
        </Card>
    )
}

function DocumentsCard({ org, app, editable }: { org: string; app: RentalApplication; editable: boolean }) {
    const { t } = useTranslation('rentalApplications')
    const label = useLabel()
    const refresh = useRefreshApplications()
    const members = Object.fromEntries(app.members.map((m) => [m.uid, m.name]))
    const criteria = Object.fromEntries(app.questionnaire.map((q) => [q.criterion_code, label(q.label, q.criterion_code)]))
    const [memberUid, setMemberUid] = useState(UNSET)
    const [criterion, setCriterion] = useState(UNSET)
    const [error, setError] = useState<string | null>(null)
    const link = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/documents', {
        onSuccess: () => {
            refresh()
            setError(null)
        },
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    const linkDocument = (documentUid: string) =>
        link.mutate({
            params: { path: { orgCode: org, applicationUid: app.uid } },
            body: {
                document_uid: documentUid,
                member_uid: memberUid === UNSET ? undefined : memberUid,
                criterion_code: criterion === UNSET ? undefined : criterion,
            },
        })
    const linked = new Set(app.documents.map((d) => d.document_uid))
    return (
        <Card data-testid="rental-application-documents">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('documents.title')}</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
                {app.documents.length === 0 && <p className="text-muted-foreground">{t('documents.none')}</p>}
                {app.documents.map((d) => (
                    <div key={d.uid} className="flex flex-wrap items-center gap-2" data-testid="rental-application-document-row" data-opened={d.opened_in_review ? 'true' : 'false'}>
                        <span className="font-medium">{d.name}</span>
                        {d.document_type_code && <Badge variant="outline">{d.document_type_code}</Badge>}
                        <span className="text-muted-foreground">
                            {d.member_uid ? members[d.member_uid] : ''}
                            {d.criterion_code ? ` · ${criteria[d.criterion_code] ?? d.criterion_code}` : ''}
                        </span>
                        <a
                            href={`${apiBaseUrl}/api/v1/organizations/${org}/documents/${d.document_uid}/content`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline"
                            onClick={() => window.setTimeout(refresh, 1500)}
                            data-testid="rental-application-document-open"
                        >
                            {t('documents.open')}
                        </a>
                        {app.open_review && (
                            <Badge variant={d.opened_in_review ? 'success' : 'warning'} data-testid="rental-application-document-opened">
                                {d.opened_in_review ? t('documents.opened') : t('documents.unopened')}
                            </Badge>
                        )}
                    </div>
                ))}
                {editable && (
                    <div className="flex flex-wrap items-end gap-2 border-t pt-3" data-testid="rental-application-document-link">
                        <div className="space-y-1">
                            <Label>{t('documents.link.member')}</Label>
                            <Select value={memberUid} onValueChange={setMemberUid}>
                                <SelectTrigger className="w-48" data-testid="rental-application-document-link-member"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={UNSET} data-testid="rental-application-document-link-member-none">{t('documents.link.whole_file')}</SelectItem>
                                    {app.members.map((m) => (
                                        <SelectItem key={m.uid} value={m.uid} data-testid={`rental-application-document-link-member-${m.uid}`}>{m.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label>{t('documents.link.criterion')}</Label>
                            <Select value={criterion} onValueChange={setCriterion}>
                                <SelectTrigger className="w-72" data-testid="rental-application-document-link-criterion"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={UNSET} data-testid="rental-application-document-link-criterion-none">{t('documents.link.no_criterion')}</SelectItem>
                                    {app.questionnaire.map((q) => (
                                        <SelectItem key={q.criterion_code} value={q.criterion_code} data-testid={`rental-application-document-link-criterion-${q.criterion_code}`}>
                                            {label(q.label, q.criterion_code)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <AttachDocumentControl
                            org={org}
                            linkedUids={linked}
                            testidPrefix="rental-application-document-link"
                            showUpload={false}
                            labels={{
                                attachExisting: t('documents.link.button'),
                                upload: '',
                                uploading: '',
                                pickDocumentTitle: t('documents.link.pick_title'),
                                searchPlaceholder: t('documents.link.search'),
                                noDocumentsFound: t('documents.link.not_found'),
                                alreadyAttached: t('documents.link.already'),
                            }}
                            onUploaded={linkDocument}
                            onSelect={linkDocument}
                        />
                        {error && <p className="w-full text-destructive" data-testid="rental-application-document-link-error">{error}</p>}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

function ReviewPanel({ org, app, canWrite }: { org: string; app: RentalApplication; canWrite: boolean }) {
    const { t } = useTranslation('rentalApplications')
    const label = useLabel()
    const refresh = useRefreshApplications()
    const [overrides, setOverrides] = useState<Overrides>({})
    const [refusing, setRefusing] = useState(false)
    const [withdrawing, setWithdrawing] = useState(false)
    const [reason, setReason] = useState('')
    const [note, setNote] = useState('')
    const [error, setError] = useState<string | null>(null)
    const path = { orgCode: org, applicationUid: app.uid }
    const fail = (err: unknown) => {
        const code = apiErrorCode(err)
        setError(code ? t(`errors.${code}`, { defaultValue: apiErrorDetail(err, t('errors.generic')) }) : apiErrorDetail(err, t('errors.generic')))
    }
    const done = () => {
        refresh()
        setError(null)
        setRefusing(false)
        setWithdrawing(false)
    }
    const start = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/reviews', { onSuccess: done, onError: fail })
    const refuse = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/refuse', { onSuccess: done, onError: fail })
    const withdraw = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/withdraw', { onSuccess: done, onError: fail })
    const advance = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/advance', { onSuccess: done, onError: fail })
    const review = app.open_review
    const unopened = app.documents.filter((d) => !d.opened_in_review)
    const cleanOverrides = Object.fromEntries(Object.entries(overrides).filter(([, o]) => o.result))
    const refusedReasons = app.screening.reasons.filter((r) => r.outcome === 'refused')
    const withdrawnReasons = app.screening.reasons.filter((r) => r.outcome === 'withdrawn')
    if (!canWrite) return null

    return (
        <Card data-testid="rental-application-review">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('review.title')}</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
                {!review ? (
                    <Button size="sm" variant="outline" onClick={() => start.mutate({ params: { path } })} data-testid="rental-application-review-start">
                        {t('review.start')}
                    </Button>
                ) : (
                    <>
                        <p className="text-muted-foreground" data-testid="rental-application-review-open">{t('review.open')}</p>
                        {unopened.length > 0 && (
                            <div data-testid="rental-application-unopened-documents">
                                <p>{t('review.unopened', { count: unopened.length })}</p>
                                <ul className="list-disc pl-5">{unopened.map((d) => <li key={d.uid}>{d.name}</li>)}</ul>
                            </div>
                        )}
                        {app.screening.rows.map((r) => (
                            <div key={r.criterion_code} className="grid gap-2 md:grid-cols-[1fr_8rem_1fr]">
                                <span>{label(r.label, r.criterion_code)}</span>
                                <Select
                                    value={overrides[r.criterion_code]?.result || UNSET}
                                    onValueChange={(v) => setOverrides({ ...overrides, [r.criterion_code]: { result: v === UNSET ? '' : v, reason: overrides[r.criterion_code]?.reason ?? '' } })}
                                >
                                    <SelectTrigger data-testid="rental-application-criterion-override"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={UNSET}>{t('review.no_override')}</SelectItem>
                                        {['pass', 'review', 'fail'].map((v) => <SelectItem key={v} value={v}>{t(`result.${v}`)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {overrides[r.criterion_code]?.result ? (
                                    <Input
                                        placeholder={t('review.override_reason')}
                                        value={overrides[r.criterion_code]?.reason ?? ''}
                                        onChange={(e) => setOverrides({ ...overrides, [r.criterion_code]: { ...overrides[r.criterion_code], reason: e.target.value } })}
                                        data-testid="rental-application-criterion-override-reason"
                                    />
                                ) : <span />}
                            </div>
                        ))}
                    </>
                )}
                <div className="flex flex-wrap gap-2">
                    {review && (
                        <Button size="sm" variant="destructive" onClick={() => { setReason(''); setNote(''); setError(null); setRefusing(true) }} data-testid="rental-application-refuse-button">
                            {t('review.refuse')}
                        </Button>
                    )}
                    {['enquiry', 'screening', 'screened'].includes(app.stage) && (
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={advance.isPending}
                            onClick={() => advance.mutate({ params: { path }, body: { review_uid: app.stage === 'screened' ? review?.uid : undefined, criterion_overrides: app.stage === 'screened' ? cleanOverrides : undefined } })}
                            data-testid="rental-application-advance-button"
                        >
                            {t(`review.advance_${app.stage}`)}
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => { setReason(''); setError(null); setWithdrawing(true) }} data-testid="rental-application-withdraw-button">
                        {t('review.withdrawn')}
                    </Button>
                </div>
                {error && !refusing && !withdrawing && <p className="text-destructive" data-testid="rental-application-review-error">{error}</p>}
            </CardContent>

            <Dialog open={refusing} onOpenChange={setRefusing}>
                <DialogContent data-testid="rental-application-refuse-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('refuse.title')}</DialogTitle>
                        <DialogDescription>{t('refuse.description')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="mb-1 block text-sm">{t('refuse.reason')}</Label>
                            <Select value={reason} onValueChange={setReason}>
                                <SelectTrigger data-testid="rental-application-refuse-reason-select"><SelectValue placeholder={t('refuse.reason_placeholder')} /></SelectTrigger>
                                <SelectContent>
                                    {refusedReasons.map((r) => <SelectItem key={r.code} value={r.code} data-testid={`rental-application-refuse-reason-${r.code}`}>{label(r.label, r.code)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-1 block text-sm">{t('refuse.note')}</Label>
                            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="rental-application-refuse-note" />
                        </div>
                        {app.screening.release?.contest_route && <p className="text-xs text-muted-foreground">{label(app.screening.release.contest_route)}</p>}
                        {error && <p className="text-sm text-destructive" data-testid="rental-application-refuse-error">{error}</p>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRefusing(false)} data-testid="rental-application-refuse-cancel">{t('cancel')}</Button>
                        <Button
                            variant="destructive"
                            disabled={!reason || refuse.isPending}
                            onClick={() => review && refuse.mutate({ params: { path }, body: { review_uid: review.uid, reason_code: reason, note: note || undefined, criterion_overrides: cleanOverrides } })}
                            data-testid="rental-application-refuse-confirm"
                        >
                            {t('refuse.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={withdrawing} onOpenChange={setWithdrawing}>
                <DialogContent data-testid="rental-application-withdraw-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('withdraw.title')}</DialogTitle>
                        <DialogDescription>{t('withdraw.description')}</DialogDescription>
                    </DialogHeader>
                    <Select value={reason} onValueChange={setReason}>
                        <SelectTrigger data-testid="rental-application-withdraw-reason-select"><SelectValue placeholder={t('refuse.reason_placeholder')} /></SelectTrigger>
                        <SelectContent>
                            {withdrawnReasons.map((r) => <SelectItem key={r.code} value={r.code}>{label(r.label, r.code)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {error && <p className="text-sm text-destructive" data-testid="rental-application-withdraw-error">{error}</p>}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setWithdrawing(false)}>{t('cancel')}</Button>
                        <Button disabled={!reason || withdraw.isPending} onClick={() => withdraw.mutate({ params: { path }, body: { reason_code: reason } })} data-testid="rental-application-withdraw-confirm">
                            {t('withdraw.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    )
}

function QuestionnaireLinkCard({ org, app, editable }: { org: string; app: RentalApplication; editable: boolean }) {
    const { t } = useTranslation('rentalApplications')
    const refresh = useRefreshApplications()
    const [open, setOpen] = useState(false)
    const [body, setBody] = useState('')
    const [error, setError] = useState<string | null>(null)
    const link = app.questionnaire_url ?? ''
    const send = $api.useMutation('post', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/questionnaire/send', {
        onSuccess: () => {
            refresh()
            setOpen(false)
        },
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    return (
        <Card data-testid="rental-application-questionnaire-link">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('send.title')}</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
                {!link ? (
                    <p className="text-muted-foreground">{t('send.no_thread')}</p>
                ) : (
                    <>
                        <Input readOnly value={link} data-testid="rental-application-questionnaire-url" />
                        {editable && (
                            <Button size="sm" variant="outline" onClick={() => { setBody(t('send.body', { link: link.replace('#questionnaire', '') })); setError(null); setOpen(true) }} data-testid="rental-application-send-questionnaire">
                                {t('send.button')}
                            </Button>
                        )}
                    </>
                )}
            </CardContent>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="rental-application-send-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('send.title')}</DialogTitle>
                        <DialogDescription>{t('send.description')}</DialogDescription>
                    </DialogHeader>
                    <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} data-testid="rental-application-send-body" />
                    {error && <p className="text-sm text-destructive" data-testid="rental-application-send-error">{error}</p>}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>{t('cancel')}</Button>
                        <Button disabled={send.isPending} onClick={() => send.mutate({ params: { path: { orgCode: org, applicationUid: app.uid } }, body: { body } })} data-testid="rental-application-send-confirm">
                            {t('send.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    )
}

function PurgeCard({ org, app, canWrite }: { org: string; app: RentalApplication; canWrite: boolean }) {
    const { t } = useTranslation('rentalApplications')
    const { formatDate } = useFormatters()
    const refresh = useRefreshApplications()
    const [hold, setHold] = useState(app.legal_hold)
    const [reason, setReason] = useState(app.legal_hold_reason ?? '')
    const [error, setError] = useState<string | null>(null)
    useEffect(() => {
        setHold(app.legal_hold)
        setReason(app.legal_hold_reason ?? '')
    }, [app.legal_hold, app.legal_hold_reason])
    const patch = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}', {
        onSuccess: () => {
            refresh()
            setError(null)
        },
        onError: (err) => setError(apiErrorDetail(err, t('errors.generic'))),
    })
    return (
        <Card data-testid="rental-application-purge">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('purge.title')}</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
                <p data-testid="rental-application-purge-after">
                    {app.purge_after ? t('purge.date', { date: formatDate(app.purge_after) }) : t('purge.retained')}
                </p>
                <label className="flex items-center gap-2">
                    <Checkbox checked={hold} disabled={!canWrite} onCheckedChange={setHold} data-testid="rental-application-legal-hold-toggle" />
                    {t('purge.legal_hold')}
                </label>
                {hold && (
                    <Input value={reason} disabled={!canWrite} placeholder={t('purge.legal_hold_reason')} onChange={(e) => setReason(e.target.value)} data-testid="rental-application-legal-hold-reason" />
                )}
                {canWrite && (hold !== app.legal_hold || reason !== (app.legal_hold_reason ?? '')) && (
                    <Button
                        size="sm"
                        disabled={patch.isPending || (hold && !reason.trim())}
                        onClick={() => patch.mutate({ params: { path: { orgCode: org, applicationUid: app.uid } }, body: { legal_hold: hold, legal_hold_reason: hold ? reason : undefined } })}
                        data-testid="rental-application-legal-hold-save"
                    >
                        {t('purge.save')}
                    </Button>
                )}
                {error && <p className="text-destructive">{error}</p>}
            </CardContent>
        </Card>
    )
}

function EventsCard({ org, applicationUid }: { org: string; applicationUid: string }) {
    const { t } = useTranslation('rentalApplications')
    const { formatDate } = useFormatters()
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/rental-applications/{applicationUid}/events', {
        params: { path: { orgCode: org, applicationUid } },
    })
    return (
        <Card data-testid="rental-application-events">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('events.title')}</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-sm">
                {(data?.events ?? []).map((e) => (
                    <div key={e.uid} className="flex flex-wrap gap-2" data-testid="rental-application-event" data-kind={e.kind}>
                        <span className="text-muted-foreground">{formatDate(e.occurred_at, { dateStyle: 'short', timeStyle: 'short' })}</span>
                        <span>{t(`event.${e.kind}`, { defaultValue: e.kind })}</span>
                        {e.from_stage && e.to_stage && <span className="text-muted-foreground">{t(`stage.${e.from_stage}`)} → {t(`stage.${e.to_stage}`)}</span>}
                        {e.reason_code && <Badge variant="outline">{e.reason_code}</Badge>}
                    </div>
                ))}
            </CardContent>
        </Card>
    )
}
